/**
 * Phase 3D: Rule-Based Detection Engine
 * 
 * Searches operative note text for CPT evidence based on
 * documentation requirements and keywords.
 * 
 * IMPORTANT: No inference, no AI-generated statements.
 * Only matches based on explicit keyword/phrase detection.
 */

import { 
  EvidenceSnippet, 
  DocumentationStatusV2,
  DOCUMENTATION_STATUS_LABELS,
} from '@/lib/phase3/types';

// ============================================================================
// TYPES
// ============================================================================

export interface DocumentationElement {
  id: string;
  label: string;
  description: string;
  keywords: string[];
  strictness: 'required' | 'recommended' | 'optional';
}

export interface CptRequirement {
  cptCode: string;
  cptDescription?: string;
  requiredElements: DocumentationElement[];
}

export interface ElementMatchResult {
  elementId: string;
  elementLabel: string;
  strictness: 'required' | 'recommended' | 'optional';
  matched: boolean;
  matchedKeywords: string[];
  snippets: EvidenceSnippet[];
}

export interface CptDetectionResult {
  cptCode: string;
  cptDescription?: string;
  detectedInNote: boolean;
  documentationStatus: DocumentationStatusV2;
  elementResults: ElementMatchResult[];
  allEvidenceSnippets: EvidenceSnippet[];
  matchedElementCount: number;
  totalRequiredElements: number;
  missingRequiredElements: string[];
}

export interface NoteAnalysisResult {
  noteLength: number;
  analyzedAt: string;
  cptResults: CptDetectionResult[];
  totalCptsAnalyzed: number;
  cptsWithEvidence: number;
  cptsWithSufficientDocs: number;
}

// ============================================================================
// KEYWORD EXPANSION
// ============================================================================

/**
 * Common orthopedic synonyms and phrase variations
 * Expands base keywords to catch common surgeon phrasing
 */
const KEYWORD_SYNONYMS: Record<string, string[]> = {
  'repair': ['repaired', 'repairing', 'fixed', 'reconstructed', 'restored'],
  'debridement': ['debrided', 'debride', 'cleaned', 'removed degenerative tissue'],
  'resection': ['resected', 'excised', 'removed', 'excision'],
  'incision': ['incised', 'opened', 'exposed', 'approach'],
  'suture': ['sutured', 'suturing', 'stitched', 'closed with sutures'],
  'anchor': ['anchors', 'anchored', 'suture anchor', 'suture anchors'],
  'burr': ['burred', 'burring', 'burring device'],
  'shaver': ['shaved', 'shaving', 'motorized shaver'],
  'decompression': ['decompressed', 'decompressing', 'released'],
  'release': ['released', 'releasing', 'divided', 'transected'],
  'meniscectomy': ['meniscus resection', 'meniscal resection', 'partial meniscectomy'],
  'chondroplasty': ['chondral debridement', 'cartilage debridement', 'cartilage smoothing'],
  'synovectomy': ['synovial debridement', 'synovitis debridement'],
  'arthroscopy': ['arthroscopic', 'arthroscopically', 'scope'],
  'portal': ['portals', 'arthroscopic portal', 'scope portal'],
  'rotator cuff': ['cuff', 'supraspinatus', 'infraspinatus', 'subscapularis', 'teres minor'],
  'labrum': ['labral', 'glenoid labrum', 'labral tear'],
  'acromioplasty': ['acromion resection', 'subacromial decompression', 'acromial spur removal'],
  'performed': ['completed', 'carried out', 'done', 'accomplished', 'executed'],
};

/**
 * Expand a keyword to include common variations
 */
function expandKeyword(keyword: string): string[] {
  const normalizedKeyword = keyword.toLowerCase().trim();
  const expansions = new Set<string>([normalizedKeyword]);
  
  // Check if this keyword has synonyms
  for (const [base, synonyms] of Object.entries(KEYWORD_SYNONYMS)) {
    if (normalizedKeyword.includes(base.toLowerCase())) {
      synonyms.forEach(syn => expansions.add(syn.toLowerCase()));
    }
    // Also check if keyword is a synonym
    if (synonyms.some(syn => syn.toLowerCase() === normalizedKeyword)) {
      expansions.add(base.toLowerCase());
      synonyms.forEach(syn => expansions.add(syn.toLowerCase()));
    }
  }
  
  return Array.from(expansions);
}

// ============================================================================
// TEXT SEARCH HELPERS
// ============================================================================

/**
 * Find all occurrences of a keyword/phrase in text
 * Returns snippets with context
 */
function findKeywordInText(
  text: string,
  keyword: string,
  contextChars: number = 80
): EvidenceSnippet[] {
  const snippets: EvidenceSnippet[] = [];
  const lowerText = text.toLowerCase();
  const lowerKeyword = keyword.toLowerCase();
  
  let startIndex = 0;
  while (true) {
    const foundIndex = lowerText.indexOf(lowerKeyword, startIndex);
    if (foundIndex === -1) break;
    
    // Extract snippet with context
    const snippetStart = Math.max(0, foundIndex - contextChars);
    const snippetEnd = Math.min(text.length, foundIndex + lowerKeyword.length + contextChars);
    
    // Find word boundaries for cleaner snippets
    const adjustedStart = snippetStart > 0 
      ? text.indexOf(' ', snippetStart) + 1 || snippetStart
      : snippetStart;
    const adjustedEnd = snippetEnd < text.length
      ? text.lastIndexOf(' ', snippetEnd) || snippetEnd
      : snippetEnd;
    
    const snippetText = text.slice(adjustedStart, adjustedEnd).trim();
    
    snippets.push({
      text: snippetText,
      offset_start: adjustedStart,
      offset_end: adjustedEnd,
      matched_keywords: [keyword],
    });
    
    startIndex = foundIndex + lowerKeyword.length;
  }
  
  return snippets;
}

/**
 * Search for a documentation element in the note text
 */
function searchForElement(
  noteText: string,
  element: DocumentationElement
): { matched: boolean; matchedKeywords: string[]; snippets: EvidenceSnippet[] } {
  const allSnippets: EvidenceSnippet[] = [];
  const matchedKeywords: string[] = [];
  
  for (const keyword of element.keywords) {
    // Expand keyword to include variations
    const expandedKeywords = expandKeyword(keyword);
    
    for (const expandedKeyword of expandedKeywords) {
      const snippets = findKeywordInText(noteText, expandedKeyword);
      if (snippets.length > 0) {
        // Update matched keywords in snippets
        snippets.forEach(s => {
          if (!s.matched_keywords.includes(keyword)) {
            s.matched_keywords = [...s.matched_keywords, keyword];
          }
        });
        allSnippets.push(...snippets);
        if (!matchedKeywords.includes(keyword)) {
          matchedKeywords.push(keyword);
        }
      }
    }
  }
  
  // Deduplicate overlapping snippets
  const deduplicatedSnippets = deduplicateSnippets(allSnippets);
  
  return {
    matched: deduplicatedSnippets.length > 0,
    matchedKeywords,
    snippets: deduplicatedSnippets,
  };
}

/**
 * Remove overlapping or duplicate snippets
 */
function deduplicateSnippets(snippets: EvidenceSnippet[]): EvidenceSnippet[] {
  if (snippets.length === 0) return [];
  
  // Sort by start offset
  const sorted = [...snippets].sort((a, b) => a.offset_start - b.offset_start);
  const result: EvidenceSnippet[] = [sorted[0]];
  
  for (let i = 1; i < sorted.length; i++) {
    const current = sorted[i];
    const previous = result[result.length - 1];
    
    // If overlapping, merge the snippets
    if (current.offset_start <= previous.offset_end) {
      // Extend the previous snippet if needed
      if (current.offset_end > previous.offset_end) {
        previous.offset_end = current.offset_end;
        previous.text = sorted[0].text; // Keep original text for simplicity
      }
      // Merge matched keywords
      current.matched_keywords.forEach(kw => {
        if (!previous.matched_keywords.includes(kw)) {
          previous.matched_keywords.push(kw);
        }
      });
    } else {
      result.push(current);
    }
  }
  
  return result;
}

// ============================================================================
// DOCUMENTATION STATUS DETERMINATION
// ============================================================================

/**
 * Determine documentation status based on element matches
 * 
 * Rules:
 * - SUFFICIENT: All required elements matched
 * - INSUFFICIENT: One or more required elements not matched
 * - AMBIGUOUS: Some required matched, some recommended missing
 * - NOT_FOUND: No matches at all
 */
function determineDocumentationStatus(
  elementResults: ElementMatchResult[]
): DocumentationStatusV2 {
  if (elementResults.length === 0) {
    return 'not_found';
  }
  
  const requiredElements = elementResults.filter(e => e.strictness === 'required');
  const recommendedElements = elementResults.filter(e => e.strictness === 'recommended');
  
  const allMatched = elementResults.every(e => e.matched);
  const anyMatched = elementResults.some(e => e.matched);
  
  const requiredMatched = requiredElements.filter(e => e.matched);
  const requiredMissing = requiredElements.filter(e => !e.matched);
  const recommendedMatched = recommendedElements.filter(e => e.matched);
  
  // No matches at all
  if (!anyMatched) {
    return 'not_found';
  }
  
  // All required elements matched
  if (requiredMissing.length === 0 && requiredElements.length > 0) {
    return 'sufficient';
  }
  
  // Some required matched, some missing = ambiguous
  if (requiredMatched.length > 0 && requiredMissing.length > 0) {
    return 'ambiguous';
  }
  
  // Only recommended elements matched (no required)
  if (requiredElements.length === 0 && recommendedMatched.length > 0) {
    return 'ambiguous';
  }
  
  // Required elements missing
  if (requiredMissing.length > 0) {
    return 'insufficient';
  }
  
  return 'ambiguous';
}

// ============================================================================
// MAIN DETECTION FUNCTION
// ============================================================================

/**
 * Analyze a single CPT against the operative note
 */
export function analyzeCptEvidence(
  noteText: string,
  cptRequirement: CptRequirement
): CptDetectionResult {
  const elementResults: ElementMatchResult[] = [];
  const allEvidenceSnippets: EvidenceSnippet[] = [];
  
  // Search for each documentation element
  for (const element of cptRequirement.requiredElements) {
    const { matched, matchedKeywords, snippets } = searchForElement(noteText, element);
    
    elementResults.push({
      elementId: element.id,
      elementLabel: element.label,
      strictness: element.strictness,
      matched,
      matchedKeywords,
      snippets,
    });
    
    allEvidenceSnippets.push(...snippets);
  }
  
  // Determine overall status
  const documentationStatus = determineDocumentationStatus(elementResults);
  const detectedInNote = allEvidenceSnippets.length > 0;
  
  // Count required elements
  const requiredElements = cptRequirement.requiredElements.filter(e => e.strictness === 'required');
  const matchedRequiredCount = elementResults.filter(
    e => e.strictness === 'required' && e.matched
  ).length;
  
  const missingRequiredElements = elementResults
    .filter(e => e.strictness === 'required' && !e.matched)
    .map(e => e.elementLabel);
  
  return {
    cptCode: cptRequirement.cptCode,
    cptDescription: cptRequirement.cptDescription,
    detectedInNote,
    documentationStatus,
    elementResults,
    allEvidenceSnippets: deduplicateSnippets(allEvidenceSnippets),
    matchedElementCount: matchedRequiredCount,
    totalRequiredElements: requiredElements.length,
    missingRequiredElements,
  };
}

/**
 * Analyze all authorized CPTs against the operative note
 */
export function analyzeNote(
  noteText: string,
  cptRequirements: CptRequirement[]
): NoteAnalysisResult {
  const cptResults = cptRequirements.map(req => analyzeCptEvidence(noteText, req));
  
  const cptsWithEvidence = cptResults.filter(r => r.detectedInNote).length;
  const cptsWithSufficientDocs = cptResults.filter(
    r => r.documentationStatus === 'sufficient'
  ).length;
  
  return {
    noteLength: noteText.length,
    analyzedAt: new Date().toISOString(),
    cptResults,
    totalCptsAnalyzed: cptResults.length,
    cptsWithEvidence,
    cptsWithSufficientDocs,
  };
}

// ============================================================================
// DEMO DATA HELPERS
// ============================================================================

/**
 * Default documentation requirements for demo CPTs
 * Used when no database requirements are defined
 */
export const DEMO_CPT_REQUIREMENTS: CptRequirement[] = [
  {
    cptCode: '29827',
    cptDescription: 'Arthroscopy, shoulder, surgical; with rotator cuff repair',
    requiredElements: [
      {
        id: 'rotator_cuff_tear',
        label: 'Rotator Cuff Tear Identified',
        description: 'Documentation of rotator cuff tear',
        keywords: ['rotator cuff tear', 'cuff tear', 'supraspinatus tear', 'full-thickness tear'],
        strictness: 'required',
      },
      {
        id: 'repair_performed',
        label: 'Repair Performed',
        description: 'Documentation of repair technique',
        keywords: ['rotator cuff repair', 'cuff repair', 'anchor', 'suture anchor', 'tendon secured'],
        strictness: 'required',
      },
      {
        id: 'arthroscopic_approach',
        label: 'Arthroscopic Approach',
        description: 'Confirmation of arthroscopic technique',
        keywords: ['arthroscopy', 'arthroscopic', 'scope', 'portal'],
        strictness: 'required',
      },
    ],
  },
  {
    cptCode: '29826',
    cptDescription: 'Arthroscopy, shoulder, surgical; decompression of subacromial space',
    requiredElements: [
      {
        id: 'subacromial_space',
        label: 'Subacromial Space Addressed',
        description: 'Work in subacromial space documented',
        keywords: ['subacromial', 'acromioplasty', 'acromion', 'decompression'],
        strictness: 'required',
      },
      {
        id: 'separate_compartment',
        label: 'Separate Anatomic Compartment',
        description: 'Distinct from primary procedure location',
        keywords: ['subacromial space', 'bursa', 'bursectomy', 'subacromial bursa'],
        strictness: 'required',
      },
    ],
  },
  {
    cptCode: '29881',
    cptDescription: 'Arthroscopy, knee, surgical; with meniscectomy',
    requiredElements: [
      {
        id: 'meniscus_pathology',
        label: 'Meniscus Pathology Identified',
        description: 'Documentation of meniscal tear or damage',
        keywords: ['meniscus tear', 'meniscal tear', 'torn meniscus', 'meniscal pathology'],
        strictness: 'required',
      },
      {
        id: 'meniscectomy_performed',
        label: 'Meniscectomy Performed',
        description: 'Resection of meniscal tissue',
        keywords: ['meniscectomy', 'meniscal resection', 'meniscus resection', 'meniscal tissue removed'],
        strictness: 'required',
      },
    ],
  },
  {
    cptCode: '29877',
    cptDescription: 'Arthroscopy, knee, surgical; debridement/shaving of articular cartilage',
    requiredElements: [
      {
        id: 'chondroplasty',
        label: 'Chondroplasty/Debridement',
        description: 'Cartilage debridement performed',
        keywords: ['chondroplasty', 'chondromalacia', 'cartilage debridement', 'shaving', 'articular cartilage'],
        strictness: 'required',
      },
    ],
  },
  {
    cptCode: '27447',
    cptDescription: 'Arthroplasty, knee, condyle and plateau; medial AND lateral compartments',
    requiredElements: [
      {
        id: 'tka_performed',
        label: 'Total Knee Arthroplasty',
        description: 'TKA procedure documented',
        keywords: ['total knee arthroplasty', 'TKA', 'knee replacement', 'knee prosthesis'],
        strictness: 'required',
      },
      {
        id: 'medial_lateral',
        label: 'Medial and Lateral Compartments',
        description: 'Both compartments addressed',
        keywords: ['medial', 'lateral', 'both compartments', 'medial and lateral'],
        strictness: 'required',
      },
      {
        id: 'components',
        label: 'Component Implantation',
        description: 'Femoral and tibial components',
        keywords: ['femoral component', 'tibial component', 'tibial baseplate', 'polyethylene'],
        strictness: 'required',
      },
    ],
  },
  {
    cptCode: '29823',
    cptDescription: 'Arthroscopy, shoulder, surgical; debridement, extensive',
    requiredElements: [
      {
        id: 'extensive_debridement',
        label: 'Extensive Debridement',
        description: 'Debridement exceeding 30 minutes',
        keywords: ['extensive debridement', 'debridement', 'exceeding 30 minutes', 'significant debridement'],
        strictness: 'required',
      },
    ],
  },
  {
    cptCode: '64721',
    cptDescription: 'Neuroplasty and/or transposition; median nerve at carpal tunnel',
    requiredElements: [
      {
        id: 'carpal_tunnel_release',
        label: 'Carpal Tunnel Release',
        description: 'Transverse carpal ligament released',
        keywords: ['carpal tunnel release', 'transverse carpal ligament', 'median nerve', 'CTR'],
        strictness: 'required',
      },
      {
        id: 'nerve_decompression',
        label: 'Nerve Decompression',
        description: 'Median nerve decompressed',
        keywords: ['median nerve', 'nerve decompression', 'complete release', 'nerve visualized'],
        strictness: 'required',
      },
    ],
  },
];

/**
 * Get requirements for CPT codes (uses demo data as fallback)
 */
export function getRequirementsForCpts(
  cptCodes: string[],
  dbRequirements?: CptRequirement[]
): CptRequirement[] {
  return cptCodes.map(code => {
    // First check database requirements
    const dbReq = dbRequirements?.find(r => r.cptCode === code);
    if (dbReq) return dbReq;
    
    // Fall back to demo requirements
    const demoReq = DEMO_CPT_REQUIREMENTS.find(r => r.cptCode === code);
    if (demoReq) return demoReq;
    
    // Return minimal requirement for unknown CPTs
    return {
      cptCode: code,
      requiredElements: [
        {
          id: `${code}_generic`,
          label: 'Procedure Documentation',
          description: 'General procedure documentation',
          keywords: [code],
          strictness: 'required' as const,
        },
      ],
    };
  });
}
